import { useState, type ReactNode } from 'react';
import { Button } from '../neon/controls';
import { useDomId } from '../hooks/useDomId';

export interface ReadItem {
  label: string;
  value: ReactNode;
}

/** Read-mode label/value display (mirrors the legacy definition-list layout). */
export function DefinitionList({ items }: { items: ReadItem[] }) {
  return (
    <dl className="def-list">
      {items.map((it, i) => (
        <div className="def-row" key={`${it.label}-${i}`}>
          <dt>{it.label}</dt>
          <dd>{it.value === '' || it.value == null ? '—' : it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface DetailSectionProps {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}

export function DetailSection({ title, actions, children }: DetailSectionProps) {
  return (
    <section className="detail-section">
      <div className="section-head">
        <h3>{title}</h3>
        {actions ? <div className="section-actions">{actions}</div> : null}
      </div>
      <div className="section-body">{children}</div>
    </section>
  );
}

export interface EditableSectionProps {
  title: string;
  read: ReactNode;
  renderEdit: (onDone: () => void) => ReactNode;
  editLabel?: string;
}

/**
 * A titled section that shows a read-only view with an "Edit" action; clicking
 * Edit swaps in an edit form (edit mode), matching the legacy read/edit split.
 */
export function EditableSection({ title, read, renderEdit, editLabel = 'Edit' }: EditableSectionProps) {
  const [editing, setEditing] = useState(false);
  const id = useDomId('edit');
  return (
    <DetailSection
      title={title}
      actions={
        !editing ? (
          <Button id={id} text={editLabel} variant="tertiary" icon="edit" onClick={() => setEditing(true)} />
        ) : undefined
      }
    >
      {editing ? renderEdit(() => setEditing(false)) : read}
    </DetailSection>
  );
}
